import { useEffect, useMemo, useState } from "react"
import { calculateMicroProfile } from "@/data/microProfiles"

const ANSWERS_KEY = "basic_answers"
const COLLAPSED_KEY = "debug_widget_collapsed"
const QUESTION_IDS_KEY = "basic_question_ids"
const ADVANCED_ANSWERS_KEY = "advanced_answers"
const ADVANCED_QUESTION_IDS_KEY = "advanced_question_ids"
const ADVANCED_QUESTION_OPTIONS_KEY = "advanced_question_option_values"

type AnswersMap = Record<string, number>

type AdvancedScores = {
  H: number
  E: number
  B: number
  A: number
  G: number
  C: number
}

type AdvancedMetric = { metric: string; sub_metric: string; text: string }
type AdvancedMetrics = Record<string, AdvancedMetric[]>

type AdvancedQuestion = {
  id: number
  options?: Array<{ value: number; text: string }>
}

type AdvancedStage = {
  questions: AdvancedQuestion[]
}

function parseAnswers(): AnswersMap {
  if (typeof window === "undefined") return {}
  const raw = window.localStorage.getItem(ANSWERS_KEY)
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, number>
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

function parseAdvancedAnswers(): AnswersMap {
  if (typeof window === "undefined") return {}
  const raw = window.localStorage.getItem(ADVANCED_ANSWERS_KEY)
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, number>
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

function computeCounts(answers: AnswersMap) {
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 }
  Object.values(answers).forEach((value) => {
    if (typeof value === "number" && value >= 1 && value <= 9) {
      counts[value] += 1
    }
  })
  return counts
}

function computeDominantType(counts: Record<number, number>): number | null {
  const entries = Object.entries(counts).map(([key, value]) => [Number(key), value] as [number, number])
  entries.sort((a, b) => b[1] - a[1])
  const top = entries[0]
  return top && top[1] > 0 ? top[0] : null
}

function computeAdvancedScores(stage: AdvancedStage, metrics: AdvancedMetrics, inputAnswers: AnswersMap) {
  const scores: AdvancedScores = { H: 0, E: 0, B: 0, A: 0, G: 0, C: 0 }

  Object.entries(inputAnswers).forEach(([questionIdStr, optionValue]) => {
    const questionId = Number(questionIdStr)
    const question = stage.questions.find((q) => q.id === questionId)
    if (!question || !question.options) return
    const metricOptions = metrics[String(questionId)]
    if (!metricOptions || metricOptions.length === 0) return
    const selectedOption = question.options.find((option) => option.value === optionValue)
    if (!selectedOption) return
    const matched = metricOptions.find((option) => option.text.trim() === selectedOption.text.trim())
    if (!matched) return
    const metricKey = matched.metric as keyof AdvancedScores
    if (metricKey in scores) {
      scores[metricKey] += 1
    }
  })

  return scores
}

export function DebugWidget() {
  const [collapsed, setCollapsed] = useState(true)
  const [answers, setAnswers] = useState<AnswersMap>({})
  const [advancedAnswers, setAdvancedAnswers] = useState<AnswersMap>({})

  useEffect(() => {
    setAnswers(parseAnswers())
    setAdvancedAnswers(parseAdvancedAnswers())
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(COLLAPSED_KEY)
      if (stored !== null) setCollapsed(stored === "true")
    }
  }, [])

  const counts = useMemo(() => computeCounts(answers), [answers])
  const answerList = useMemo(() => {
    const entries = Object.entries(answers)
      .filter(([key]) => !Number.isNaN(Number(key)))
      .map(([key, value]) => ({ id: Number(key), answer: value }))
      .sort((a, b) => a.id - b.id)
    return entries.slice(0, 45)
  }, [answers])

  const toggle = () => {
    const next = !collapsed
    setCollapsed(next)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COLLAPSED_KEY, String(next))
    }
  }

  const refresh = () => {
    setAnswers(parseAnswers())
    setAdvancedAnswers(parseAdvancedAnswers())
  }

  const randomizeAnswers = async () => {
    if (typeof window === "undefined") return
    const rawIds = window.localStorage.getItem(QUESTION_IDS_KEY)
    if (!rawIds) {
      window.alert("IDs das questões não encontrados. Abra /questions uma vez.")
      return
    }
    let ids: number[] = []
    try {
      const parsed = JSON.parse(rawIds)
      if (Array.isArray(parsed)) {
        ids = parsed.filter((id) => typeof id === "number")
      }
    } catch {
      ids = []
    }

    if (ids.length === 0) {
      window.alert("IDs das questões inválidos.")
      return
    }

    const nextAnswers: AnswersMap = {}
    ids.forEach((id) => {
      nextAnswers[String(id)] = Math.floor(Math.random() * 9) + 1
    })

    window.localStorage.setItem(ANSWERS_KEY, JSON.stringify(nextAnswers))
    setAnswers(nextAnswers)

    const counts = computeCounts(nextAnswers)
    const dominant = computeDominantType(counts)

    try {
      const response = await fetch("/test-results", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          test_result: {
            completo: true,
            tipo_eneagrama: dominant,
            pontuacoes_eneagrama: counts,
            respostas: nextAnswers
          }
        })
      })
      if (!response.ok) {
        window.alert("Falha ao salvar resultado básico de debug.")
      }
    } catch {
      window.alert("Erro ao salvar resultado básico de debug.")
    }

    window.location.href = "/results"
  }

  const clearAnswers = () => {
    if (typeof window === "undefined") return
    window.localStorage.removeItem(ANSWERS_KEY)
    setAnswers({})
  }

  const randomizeAdvancedAnswers = async () => {
    if (typeof window === "undefined") return
    const rawIds = window.localStorage.getItem(ADVANCED_QUESTION_IDS_KEY)
    const rawOptions = window.localStorage.getItem(ADVANCED_QUESTION_OPTIONS_KEY)
    if (!rawIds) {
      window.alert("IDs das questões avançadas não encontrados. Abra /advanced-questions uma vez.")
      return
    }
    let ids: number[] = []
    let optionsMap: Record<string, number[]> = {}
    try {
      const parsed = JSON.parse(rawIds)
      if (Array.isArray(parsed)) {
        ids = parsed.filter((id) => typeof id === "number")
      }
    } catch {
      ids = []
    }
    if (rawOptions) {
      try {
        const parsed = JSON.parse(rawOptions)
        if (parsed && typeof parsed === "object") {
          optionsMap = parsed as Record<string, number[]>
        }
      } catch {
        optionsMap = {}
      }
    }

    if (ids.length === 0) {
      window.alert("IDs das questões avançadas inválidos.")
      return
    }

    const nextAnswers: AnswersMap = {}
    ids.forEach((id) => {
      const optionValues = optionsMap[String(id)]
      if (optionValues && optionValues.length > 0) {
        const randomIndex = Math.floor(Math.random() * optionValues.length)
        nextAnswers[String(id)] = optionValues[randomIndex]
      } else {
        nextAnswers[String(id)] = Math.floor(Math.random() * 4) + 1
      }
    })

    window.localStorage.setItem(ADVANCED_ANSWERS_KEY, JSON.stringify(nextAnswers))
    setAdvancedAnswers(nextAnswers)

    try {
      const response = await fetch("/advanced-questions", {
        headers: {
          "X-Inertia": "true",
        }
      })
      if (!response.ok) {
        window.alert("Falha ao carregar métricas do teste avançado.")
        return
      }
      const data = await response.json()
      const stage = data?.props?.stage as AdvancedStage | undefined
      const advancedMetrics = data?.props?.advanced_metrics as AdvancedMetrics | undefined
      if (!stage || !advancedMetrics) {
        window.alert("Métricas do teste avançado indisponíveis.")
        return
      }

      const scores = computeAdvancedScores(stage, advancedMetrics, nextAnswers)
      const microProfile = calculateMicroProfile(scores)

      const saveResponse = await fetch("/advanced-test-results", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          advanced_test_result: {
            completo: true,
            pontuacoes: scores,
            respostas: nextAnswers,
            microperfil: microProfile.name
          }
        })
      })
      if (!saveResponse.ok) {
        window.alert("Falha ao salvar resultado avançado de debug.")
        return
      }
    } catch {
      window.alert("Erro ao salvar resultado avançado de debug.")
      return
    }

    window.location.href = "/advanced-results"
  }

  const clearAdvancedAnswers = () => {
    if (typeof window === "undefined") return
    window.localStorage.removeItem(ADVANCED_ANSWERS_KEY)
    setAdvancedAnswers({})
  }

  return (
    <div className="fixed top-3 right-3 z-50 text-xs">
      <button
        type="button"
        onClick={toggle}
        className="rounded-full bg-black/80 text-white px-3 py-1 shadow-md hover:bg-black"
      >
        Debug
      </button>

      {!collapsed && (
        <div className="mt-2 w-[320px] max-h-[70vh] overflow-auto rounded-lg bg-white/95 text-foreground shadow-lg border border-border p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">Eneagrama Debug</h4>
            <button
              type="button"
              onClick={refresh}
              className="text-xs text-primary hover:underline"
            >
              Atualizar
            </button>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={randomizeAnswers}
              className="flex-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
            >
              Respostas aleatórias
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = "/results"
              }}
              className="flex-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
            >
              Ir para resultados
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              window.location.href = "/advanced-welcome"
            }}
            className="w-full rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
          >
            Ir para teste avançado
          </button>
          <button
            type="button"
            onClick={clearAnswers}
            className="w-full rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
          >
            Limpar respostas
          </button>

          <div>
            <p className="font-semibold mb-1">Contagem por tipo</p>
            <pre className="whitespace-pre-wrap text-[11px] leading-snug bg-muted/40 rounded p-2">
{JSON.stringify(counts, null, 2)}
            </pre>
          </div>

          <div>
            <p className="font-semibold mb-1">Respostas (45)</p>
            <pre className="whitespace-pre-wrap text-[11px] leading-snug bg-muted/40 rounded p-2">
{JSON.stringify(answerList, null, 2)}
            </pre>
          </div>

          <div className="pt-2 border-t border-border/60 space-y-2">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={randomizeAdvancedAnswers}
                className="flex-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
              >
                Aleatórias avançado
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = "/advanced-results"
                }}
                className="flex-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
              >
                Ver resultado avançado
              </button>
            </div>
            <button
              type="button"
              onClick={clearAdvancedAnswers}
              className="w-full rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
            >
              Limpar respostas avançadas
            </button>

            <div>
              <p className="font-semibold mb-1">Respostas avançadas (25)</p>
              <pre className="whitespace-pre-wrap text-[11px] leading-snug bg-muted/40 rounded p-2">
{JSON.stringify(Object.entries(advancedAnswers)
  .filter(([key]) => !Number.isNaN(Number(key)))
  .map(([key, value]) => ({ id: Number(key), answer: value }))
  .sort((a, b) => a.id - b.id)
  .slice(0, 25), null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
